import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { StepWizard } from './components/StepWizard';
import { TopNav } from './components/TopNav';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TermsOfService } from './components/TermsOfService';
import { ProfilePage } from './components/ProfilePage';
import { PlannedTripsPage } from './components/PlannedTripsPage';
import { PlannedTripDetailPage } from './components/PlannedTripDetailPage';
import { TripHistoryPage } from './components/TripHistoryPage';
import { EmergencyPage } from './components/EmergencyPage';
import { MyVehiclePage } from './components/MyVehiclePage';
import { ServiciosEnRutaPage } from './components/ServiciosEnRutaPage';
import { Direction, Step, WeatherInfo, TrafficDataPoint, HourlyWeather } from './types';
import { ArrowRight, Calendar as CalendarIcon, ChevronDown, Clock, Sun, CloudRain, MapPin, CheckCircle, Car, AlertTriangle, TrendingUp, Layers, GitMerge, Map, Compass, Loader2, CloudSun, Droplets, Wind, Bell, Smartphone, Users, XCircle, UserCheck } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SmartAppBanner } from './components/SmartAppBanner';
import { trackEvent } from './lib/posthog';

// Lazy load componentes pesados para code splitting
const TrafficChart = lazy(() => import('./components/TrafficChart').then(module => ({ default: module.TrafficChart })));
const AuthModal = lazy(() => import('./components/AuthModal').then(module => ({ default: module.AuthModal })));
const CommunityTripsChart = lazy(() => import('./components/CommunityTripsChart').then(module => ({ default: module.CommunityTripsChart })));
import { directionToApi } from './lib/api';
import { ROUTES, ROUTE_COORDINATES } from './lib/constants';
import { getFormattedDate } from './lib/weather';
import { getChileNow } from './lib/date';
import { validateTrip } from './lib/validation';
import { formatDuration, departureTimeToTime, timeToDepartureTime } from './lib/utils';
import { useWeather } from './hooks/useWeather';
import { useTrip } from './hooks/useTrip';
import { useTripCount } from './hooks/useTripCount';
import { isPreviewEnvironment } from './lib/envUtils';
import { TripResponse, directionFromApi, tripsApi } from './lib/api';
import { logger } from './lib/logger';


const stepFromPath = (pathname: string): Step => {
  const normalized = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  switch (normalized) {
    case '/privacidad':
      return 'privacy';
    case '/terminos':
      return 'terms';
    default:
      return 'landing';
  }
};

const pathForStep = (step: Step): string => {
  switch (step) {
    case 'privacy':
      return '/privacidad';
    case 'terms':
      return '/terminos';
    default:
      return '/';
  }
};

const addDaysToYmd = (dateStr: string, days: number): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

// --- PREVIEW BANNER COMPONENT ---
const PreviewBanner = () => {
  if (!isPreviewEnvironment()) {
    return null;
  }

  return (
    <div className="bg-amber-500 text-white text-center py-2 px-4 text-sm font-semibold sticky top-0 z-[60] shadow-md">
      🧪 ENTORNO DE PREVIEW - Este es un entorno de pruebas
    </div>
  );
};

// --- COMMUNITY MESSAGE COMPONENT ---
const CommunityMessage = () => {
  // Pool de iniciales creíbles
  const INITIALS_POOL = [
    "J.P.", "M.R.", "A.S.", "C.L.", "F.G.", 
    "P.M.", "R.T.", "L.V.", "D.C.", "N.H.",
    "S.B.", "K.M.", "T.A.", "L.F.", "G.S.",
    "M.G.", "J.L.", "A.M.", "C.R.", "P.S."
  ];

  // Paleta de colores para los avatares
  const AVATAR_COLORS = [
    'bg-blue-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-cyan-500',
    'bg-teal-500',
  ];

  // Seleccionar 2-3 iniciales aleatorias (se genera una vez por render)
  const shuffle = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const selected = shuffle(INITIALS_POOL).slice(0, Math.random() < 0.5 ? 2 : 3);

  return (
    <div className="flex items-center gap-3">
      {/* Avatares superpuestos */}
      <div className="flex items-center">
        {selected.map((initial, index) => (
          <div
            key={index}
            className={`w-10 h-10 rounded-full ${AVATAR_COLORS[index % AVATAR_COLORS.length]} border-2 border-white shadow-md flex items-center justify-center text-white text-xs font-bold ${index > 0 ? '-ml-3' : ''}`}
            style={{ zIndex: selected.length - index }}
          >
            {initial}
          </div>
        ))}
      </div>
      {/* Mensaje */}
      <p className="text-sm text-gray-700">
        <span className="font-semibold">Ya muchos usuarios como tú</span> registran su viaje en esta ruta
      </p>
    </div>
  );
};

// --- TOAST COMPONENT ---
const Toast = ({ message, type, onClose }: { message: string, type: 'success' | 'error', onClose: () => void }) => (
    <div className={`fixed bottom-6 left-1/2 transform -translate-x-1/2 px-6 py-4 rounded-full shadow-2xl flex items-center gap-3 z-[100] animate-fade-in ${type === 'success' ? 'bg-gray-900 text-white' : 'bg-red-500 text-white'}`}>
        {type === 'success' ? <CheckCircle size={20} /> : <XCircle size={20} />}
        <span className="font-bold text-sm">{message}</span>
    </div>
);

function AppContent() {
  const [step, setStep] = useState<Step>(() => stepFromPath(window.location.pathname));
  const [emergencyOrigin, setEmergencyOrigin] = useState<'profile' | 'services'>('profile');
  const { user } = useAuth();
  
  // Auth Modal State
  const [isAuthModalOpen, setAuthModalOpen] = useState(false);
  const [authView, setAuthView] = useState<'login' | 'register'>('login');

  // Notification State
  const [toast, setToast] = useState<{message: string, type: 'success' | 'error'} | null>(null);
  const [selectedPlannedTrip, setSelectedPlannedTrip] = useState<TripResponse | null>(null);
  const [editingTripId, setEditingTripId] = useState<string | null>(null);

  // Core State
  const [selectedRouteId, setSelectedRouteId] = useState<string>('68');
  const [direction, setDirection] = useState<Direction>(Direction.SANTIAGO_LITORAL);
  
  const chileNow = getChileNow();
  const today = chileNow.date;
  const [tripDate, setTripDate] = useState<string>(today);
  const getMinDepartureTimeForDate = (dateStr: string) => {
    const chile = getChileNow();
    const isToday = dateStr === chile.date;
    return isToday
      ? (chile.minutes < 30 ? chile.hours + 0.5 : Math.min(23.5, chile.hours + 1))
      : 0;
  };

  // Planning State
  const [departureTime, setDepartureTime] = useState<number | null>(null);
  const [showNavigationForecast, setShowNavigationForecast] = useState(true);
  const [smartAlerts, setSmartAlerts] = useState<boolean>(true);

  // Ajustar hora mínima solo cuando ya hay una hora seleccionada por el usuario.
  // Si no hay selección, se mantiene sin preselección en el paso de horario.
  useEffect(() => {
    if (departureTime === null) return;
    const minAllowed = getMinDepartureTimeForDate(tripDate);
    if (departureTime < minAllowed) {
      setDepartureTime(minAllowed);
    }
  }, [tripDate, departureTime]);

  useEffect(() => {
    const onPopState = () => {
      setStep(stepFromPath(window.location.pathname));
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    const targetPath = pathForStep(step);
    const currentPath = window.location.pathname;
    if (currentPath !== targetPath) {
      window.history.pushState({}, '', targetPath);
    }
  }, [step]);

  // Computed
  const selectedRoute = ROUTES.find(r => r.id === selectedRouteId) || ROUTES[0];
  
  // Solo ejecutar hooks que hacen peticiones cuando el usuario está en steps relevantes
  // Esto evita peticiones innecesarias a Google Maps en la landing page y selection
  // useWeather se activa desde selection (para mostrar pronóstico del clima)
  // useTrip solo se activa desde planning (donde se necesita tiempo estimado y gráfico de tráfico)
  const shouldFetchWeather = step === 'planning' || step === 'confirmation';
  const shouldFetchTrip = step === 'planning' || step === 'confirmation';

  // Use Weather Hook (solo cuando sea necesario)
  const {
    weatherForecast,
    loadingWeather,
    selectedWeatherIndex,
    setSelectedWeatherIndex,
    currentDayHourlyWeather,
    visibleWeatherDays,
  } = useWeather({
    selectedRouteId,
    tripDate,
    onDateChange: setTripDate,
    enabled: shouldFetchWeather,
  });

  // Use Trip Hook (solo cuando sea necesario - planning y confirmation)
  const {
    isSaving,
    saveTrip,
    trafficData,
    predictionMeta,
    refetchTrafficData,
    loadingTrafficData,
    etaData,
    loadingEta,
    fallbackDurationMin,
  } = useTrip({
    tripDate,
    direction,
    selectedRouteId,
    departureTime,
    enabled: shouldFetchTrip,
  });

  const selectedTrafficPoint = departureTime === null
    ? null
    : trafficData.find((point) => {
        const selectedHour = Math.floor(departureTime);
        const selectedHalfHour = departureTime % 1 >= 0.5 ? 30 : 0;
        return point.hour === selectedHour && point.halfHour === selectedHalfHour;
      }) || null;
  const realtimeForecastMaxDate = addDaysToYmd(today, 3);
  const canConsultRealtimeForecast = tripDate <= realtimeForecastMaxDate;

  // El backend ahora normaliza internamente best_guess_min/optimistic_min/pessimistic_min
  // según el patrón de severidad horaria (ponderado por cuánta data histórica real hay
  // detrás, vía data_confidence) — ver _apply_pattern_normalization en app/services/traffic.py
  // del repo api. El frontend ya no necesita reposicionar ni ensanchar nada, solo mostrar
  // los valores tal cual llegan.
  const mostLikelyEtaMin = etaData?.eta.best_guess_min ?? null;
  const displayOptimisticMin = etaData?.eta.optimistic_min ?? null;
  const displayPessimisticMin = etaData?.eta.pessimistic_min ?? null;

  // Trip Count for confirmation screen (solo por día, solo cuando sea necesario)
  const { tripCount, loading: loadingTripCount } = useTripCount({
    tripDate,
    routeId: selectedRouteId,
    direction,
    enabled: step === 'confirmation',
  });

  // Toast Helper
  const showToast = (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
      setTimeout(() => setToast(null), 3000);
  };

  // --- SAVE TRIP LOGIC ---
  const saveTripToSupabase = async () => {
      if (departureTime === null) {
          showToast('Selecciona una hora de ingreso para continuar', 'error');
          return;
      }
      // Validar datos antes de guardar
      const validation = validateTrip(tripDate, departureTime, selectedRouteId, direction);
      if (!validation.isValid) {
          showToast(validation.error || 'Datos inválidos', 'error');
          return;
      }

      try {
          const result = await saveTrip({
              userId: user?.id || null,
              tripId: editingTripId || undefined,
              routeId: selectedRouteId,
              tripDate,
              departureTime,
              direction,
              smartAlerts: user ? smartAlerts : false, // Solo activar alertas si está logueado
              estimatedDurationMinutes: etaData?.eta.best_guess_min || fallbackDurationMin || undefined,
          });

          if (result.success) {
              showToast(
                editingTripId
                  ? 'Viaje actualizado correctamente'
                  : '¡Viaje registrado! Tu aporte ayuda a la comunidad',
                'success'
              );
              refetchTrafficData();
          } else {
              showToast(result.error || 'No se pudo guardar el viaje', 'error');
          }
      } catch (error) {
          showToast('Error inesperado al guardar. Por favor intenta de nuevo.', 'error');
      }
  };


  // Trackear eventos de navegación entre pantallas
  useEffect(() => {
    if (step === 'planning') {
      trackEvent('planning_viewed', {
        routeId: selectedRouteId,
        direction,
        tripDate,
      });
    } else if (step === 'confirmation') {
      trackEvent('confirmation_viewed', {
        routeId: selectedRouteId,
        direction,
        tripDate,
        departureTime,
        isAuthenticated: !!user,
      });
      saveTripToSupabase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, user]);

  // Computed weather state
  const selectedWeatherByDate = weatherForecast.find(d => d.rawDate === tripDate);
  const statusText = loadingWeather ? 'Cargando pronóstico...' : 'Sin conexión con el clima';
  const selectedWeather = selectedWeatherByDate || {
      day: '-', date: '-', avgTemp: 0, minTemp: 0, maxTemp: 0, condition: 'sunny', fullDate: getFormattedDate(tripDate) || statusText, rawDate: tripDate, hourly: []
  };
  
  const handleContinue = () => {
    if (step === 'selection') {
      // Entrar al paso de horario sin hora preseleccionada.
      // El slider mostrará el mínimo permitido como referencia visual.
      setDepartureTime(null);
      trackEvent('continue_button_clicked', {
        from: 'selection',
        to: 'planning',
        routeId: selectedRouteId,
        direction,
        tripDate,
      });
      setStep('planning');
    } else if (step === 'planning') {
      if (departureTime === null) {
        showToast('Selecciona una hora de ingreso para continuar', 'error');
        return;
      }
      trackEvent('continue_button_clicked', {
        from: 'planning',
        to: 'confirmation',
        routeId: selectedRouteId,
        direction,
        tripDate,
        departureTime,
      });
      setStep('confirmation');
    }
  };

  useEffect(() => {
    window.sessionStorage.setItem('ptv_trip_date', tripDate);
  }, [tripDate]);

  const handleBack = () => {
    if (step === 'planning') setStep('selection');
    if (step === 'confirmation') setStep('planning');
  };

  const handleOpenAuth = (view: 'login' | 'register') => {
      setAuthView(view);
      setAuthModalOpen(true);
  };

  const handleStartTrip = () => {
      setEditingTripId(null);
      setStep('selection');
  };

  const handleEditTripFromPlanned = (trip: TripResponse) => {
      setSelectedPlannedTrip(null);
      setEditingTripId(trip.id);
      setSelectedRouteId(trip.routeId);
      setDirection(directionFromApi(trip.direction));
      setTripDate(trip.travelDate);
      setDepartureTime(timeToDepartureTime(trip.scheduledTime));
      setStep('planning');
  };

  const handleOpenPlannedTripDetail = (trip: TripResponse) => {
      setSelectedPlannedTrip(trip);
      setStep('planned-trip-detail');
  };

  const handleBackToPlannedTrips = () => {
      setStep('planned-trips');
  };

  const handleDeletePlannedTrip = async (tripId: string): Promise<boolean> => {
    try {
      await tripsApi.delete(tripId);
      showToast('Viaje eliminado', 'success');
      return true;
    } catch (error) {
      logger.error(
        'No se pudo eliminar el viaje planificado',
        error instanceof Error ? error : new Error(String(error)),
        { tripId }
      );
      showToast('No se pudo eliminar el viaje', 'error');
      return false;
    }
  };

  const handleOpenPlannedTrips = () => {
      setStep('planned-trips');
  };

  const handleOpenTripHistory = () => {
      setStep('trip-history');
  };

  // Scroll suave al contenido principal cuando se entra al paso de planificación
  // Solo en desktop, ya que el usuario siempre llega desde 'selection'
  useEffect(() => {
    if (step === 'planning') {
      const isDesktop = window.innerWidth >= 1024; // lg breakpoint
      if (isDesktop) {
        // Delay para que la transición del paso sea suave
        const timer = setTimeout(() => {
          const weatherSection = document.getElementById('weather-comparison-section');
          if (weatherSection) {
            // Obtener posición del elemento
            const rect = weatherSection.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            // Scroll con offset de 100px para posicionamiento balanceado
            const targetPosition = rect.top + scrollTop - 100;
            
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [step]); // Ejecutar cuando cambia el step

  const handleWeatherSelect = (dayInfo: WeatherInfo) => {
      const realIndex = weatherForecast.findIndex(d => d.rawDate === dayInfo.rawDate);
      if (realIndex !== -1) {
        setSelectedWeatherIndex(realIndex);
        if (weatherForecast[realIndex]?.rawDate) {
            const newDate = weatherForecast[realIndex].rawDate!;
            setTripDate(newDate);
        }
      }
  };

  const getTrafficStatus = (trafficLevel: string | null) => {
    // Basado en el nivel de tráfico real de Google Maps
    if (!trafficLevel) return { text: 'Normal', description: 'Sin datos disponibles', color: 'text-gray-600', bg: 'bg-gray-100', border: 'border-gray-200' };
    if (trafficLevel === 'LIGHT') return { text: 'Fluido', description: 'Sin demoras', color: 'text-green-600', bg: 'bg-green-100', border: 'border-green-200' };
    if (trafficLevel === 'MODERATE') return { text: 'Medio', description: 'Con demoras moderadas', color: 'text-yellow-600', bg: 'bg-yellow-100', border: 'border-yellow-200' };
    if (trafficLevel === 'HEAVY') return { text: 'Alto', description: '+30 min demora', color: 'text-orange-600', bg: 'bg-orange-100', border: 'border-orange-200' };
    return { text: 'Muy Alto', description: '+45 min demora', color: 'text-red-600', bg: 'bg-red-100', border: 'border-red-200' };
  };

const formatTime = (val: number) => {
    const totalMin = Math.round(((val % 24) + 24) % 24 * 60);
    const h = Math.floor(totalMin / 60) % 24;
    const m = totalMin % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  const renderWeatherIcon = (condition: string, size = 20) => {
    switch(condition) {
      case 'sunny': return <Sun size={size} className="text-amber-500 fill-amber-500" />;
      case 'rainy': return <CloudRain size={size} className="text-blue-400 fill-blue-100" />;
      default: return <CloudSun size={size} className="text-gray-400 fill-gray-200" />;
    }
  };

  const renderLanding = () => (
    <div className="animate-fade-in">
        <section className="relative overflow-hidden bg-white pb-8 pt-12 md:pb-16 md:pt-20">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100/50 via-gray-50 to-white -z-10"></div>
            
            <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-8">
                    <p className="text-sm md:text-base text-gray-500 mb-4 font-medium">
                        ¿Viajas al litoral o desde el litoral central?
                    </p>
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-black text-gray-900 leading-tight tracking-tight mb-1">
                        CONGESTIÓN EN AUTOPISTAS INTER URBANAS
                    </h1>
                    <h2 className="text-xl md:text-2xl lg:text-3xl font-black leading-tight tracking-tight mb-4">
                        <span className="text-blue-600">PROBLEMA DE MUCHOS,</span> A SOLUCIONAR ENTRE TODOS
                    </h2>
                    <div className="space-y-4">
                        <p className="text-xl text-gray-500 leading-relaxed max-w-lg">
                            Para que viajes informado y sin congestión
                        </p>
                        <button
                            onClick={() => setStep('how-it-works')}
                            className="px-6 py-3 bg-white text-gray-700 border border-gray-200 rounded-xl font-bold text-base hover:bg-gray-50 hover:border-gray-300 transition-all"
                        >
                            Cómo funciona
                        </button>
                    </div>

                    {/* Store badges */}
                    <div className="space-y-2 pt-6">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Disponible en</p>
                        <div className="flex flex-wrap gap-3">
                            <a
                                href="https://play.google.com/store/apps/details?id=cl.programatuviaje.app"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all group"
                            >
                                {/* Google Play icon */}
                                <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0" fill="none">
                                    <path d="M3.18 1.02C2.47 1.4 2 2.15 2 3.04v17.92c0 .89.47 1.64 1.18 2.02l.1.05 10.04-10.04v-.24L3.28.97l-.1.05z" fill="#EA4335"/>
                                    <path d="M16.68 16.42l-3.35-3.35v-.24l3.35-3.35.08.04 3.97 2.25c1.13.64 1.13 1.69 0 2.34l-3.97 2.27-.08.04z" fill="#FBBC04"/>
                                    <path d="M16.76 16.38L13.32 12.93 3.18 23.02c.37.4.96.44 1.63.07l11.95-6.71" fill="#34A853"/>
                                    <path d="M16.76 7.62L4.81.91C4.14.54 3.55.58 3.18.98l10.14 10.09 3.44-3.45z" fill="#4285F4"/>
                                </svg>
                                <div>
                                    <div className="text-[10px] text-gray-400 leading-none">Disponible en</div>
                                    <div className="text-sm font-bold text-gray-800 leading-tight">Google Play</div>
                                </div>
                            </a>
                            <a
                                href="https://apps.apple.com/co/app/programatuviaje-cl/id6761294424"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all group"
                            >
                                <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0 text-gray-800" fill="currentColor">
                                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                                </svg>
                                <div>
                                    <div className="text-[10px] text-gray-400 leading-none">Disponible en</div>
                                    <div className="text-sm font-bold text-gray-800 leading-tight">App Store</div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>

                <div className="relative hidden lg:block">
                    <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100 p-6 rotate-3 hover:rotate-0 transition-transform duration-500 z-10">
                        <div className="flex justify-between items-center mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                    <Car size={24} />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-900">Ruta 68</h4>
                                    <p className="text-xs text-gray-500">Santiago → Litoral</p>
                                </div>
                            </div>
                            <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold">Fluido</span>
                        </div>
                        <div className="h-32 bg-gray-50 rounded-xl w-full flex items-end justify-between p-4 gap-1">
                             {[40, 60, 30, 80, 50, 20, 40].map((h, i) => (
                                 <div key={i} className="w-full bg-blue-200 rounded-t-sm" style={{height: `${h}%`}}></div>
                             ))}
                        </div>
                        <div className="mt-4 flex justify-between items-center">
                            <div className="text-sm text-gray-500">Tiempo estimado</div>
                            <div className="font-bold text-gray-900">1h 20min</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        
        {/* Features Section */}
        <section className="py-20 bg-gray-50 border-t border-gray-100">
            <div className="max-w-6xl mx-auto px-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { icon: <TrendingUp size={32} />, title: "Anticipación Real", desc: "Pasamos de un enfoque reactivo a uno predictivo. Analizamos patrones históricos para mostrarte el futuro de la ruta." },
                        { icon: <MapPin size={32} />, title: "Contexto 100% Local", desc: "Las apps globales no saben de 'puentes' ni eventos regionales. Nosotros integramos el contexto local chileno." },
                        { icon: <Users size={32} />, title: "Decisión Colectiva", desc: "Si distribuimos mejor las horas de salida, aplanamos la curva de congestión y eliminamos los momentos de saturación." }
                    ].map((feature, idx) => (
                        <div key={idx} className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm hover:shadow-lg transition-all group">
                            <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                {feature.icon}
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                            <p className="text-gray-500 leading-relaxed">{feature.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* FAQs Section */}
        <section className="py-20 bg-white border-t border-gray-100">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Preguntas Frecuentes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* FAQ 1 */}
              <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  ¿Sirve tanto para ir como para volver?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Sí. Programa Tu Viaje te ayuda a planificar viajes desde Santiago al litoral y también el regreso hacia Santiago.
                </p>
              </div>
              
              {/* FAQ 2 */}
              <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  ¿Muestra tráfico en tiempo real?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Actualmente el foco está en ayudarte a decidir antes de salir, usando datos históricos y contexto. En el futuro se incorporará visualización de tráfico en tiempo real como complemento.
                </p>
              </div>
              
              {/* FAQ 3 */}
              <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  ¿Reemplaza a Google Maps o Waze?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  No. Programa Tu Viaje te recomienda cuándo salir y luego te envía a tu app de navegación favorita.
                </p>
              </div>
              
              {/* FAQ 4 */}
              <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  ¿Es gratis?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Sí, actualmente el uso es gratuito.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer con enlaces legales */}
        <footer className="bg-gray-50 border-t border-gray-200 py-8">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 text-sm text-gray-600">
              <a
                href="/privacidad"
                onClick={() => setStep('privacy')}
                className="hover:text-gray-900 transition-colors underline"
              >
                Política de Privacidad
              </a>
              <span className="hidden sm:inline">•</span>
              <a
                href="/terminos"
                onClick={() => setStep('terms')}
                className="hover:text-gray-900 transition-colors underline"
              >
                Condiciones de Servicio
              </a>
            </div>
          </div>
        </footer>
    </div>
  );

  const renderHowItWorks = () => (
    <div className="animate-fade-in pb-20">
        <div className="bg-gray-900 text-white py-20 px-6">
            <div className="max-w-4xl mx-auto text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-800 rounded-full text-blue-400 font-bold text-xs uppercase tracking-wider mb-6">
                    <GitMerge size={14} /> Navegación + Estrategia
                </div>
                <h2 className="text-4xl md:text-5xl font-black mb-6 leading-tight">Navegación + Estrategia</h2>
                <p className="text-lg md:text-xl text-gray-400 font-medium">Para que decidas la mejor opción para tu viaje</p>
            </div>
        </div>
        <div className="max-w-5xl mx-auto px-6 -mt-12 relative z-10">
            <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-2xl shadow-gray-200/50">
                <h3 className="text-2xl md:text-3xl font-black text-gray-900 text-center mb-12">Registra tu viaje en 3 simples pasos</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
                    <div className="hidden md:block absolute top-10 left-[16.67%] right-[16.67%] h-0.5 bg-blue-200"></div>
                    {[
                        { icon: <MapPin size={24} />, step: "1", title: "Elige tu ruta", desc: "Selecciona la autopista, el sentido y la fecha de tu viaje" },
                        { icon: <Clock size={24} />, step: "2", title: "Escoge tu horario", desc: "Revisa la proyección de tráfico y elige la mejor hora para salir" },
                        { icon: <CheckCircle size={24} />, step: "3", title: "Confirma tu viaje", desc: "Registra tu viaje y activa alertas si lo deseas" },
                    ].map((item, idx) => (
                        <div key={idx} className="flex flex-col items-center text-center relative">
                            <div className="w-20 h-20 rounded-full bg-blue-50 border-2 border-blue-200 flex items-center justify-center text-blue-600 mb-4 relative z-10 bg-white">
                                {item.icon}
                            </div>
                            <span className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">Paso {item.step}</span>
                            <h4 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h4>
                            <p className="text-gray-500 text-sm leading-relaxed max-w-xs">{item.desc}</p>
                        </div>
                    ))}
                </div>
                <p className="text-center text-sm text-gray-400 mt-10">Puedes explorar sin registrarte, pero al crear tu cuenta accedes a alertas y proyecciones personalizadas.</p>
            </div>
        </div>
        {/* Sección puente: Por qué funciona */}
        <div className="max-w-5xl mx-auto px-6 mt-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="flex gap-5 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center shrink-0 text-gray-600"><Layers size={24} /></div>
                    <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-1">Integración Global</h4>
                        <p className="text-gray-500 text-sm leading-relaxed">Usamos algoritmos de tráfico de plataformas consolidadas como base para nuestras proyecciones.</p>
                    </div>
                </div>
                <div className="flex gap-5 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0 text-blue-600"><MapPin size={24} /></div>
                    <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-1">Contexto Local</h4>
                        <p className="text-gray-500 text-sm leading-relaxed">Sumamos lo que las apps globales no saben: feriados, eventos y clima regional chileno.</p>
                    </div>
                </div>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-8 text-center text-white mb-16">
                <Compass size={32} className="text-blue-200 mx-auto mb-4" />
                <p className="text-lg md:text-xl font-medium text-blue-100 leading-relaxed max-w-2xl mx-auto italic">
                    "No somos un mapa para que sepas dónde doblar. Somos la herramienta para que decidas <strong className="text-white font-bold">cuándo prender el auto.</strong>"
                </p>
            </div>
        </div>

        {/* Video explicativo */}
        <div className="max-w-3xl mx-auto px-6 mb-16">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-6">Mira cómo funciona</h3>
            <div className="rounded-2xl overflow-hidden shadow-xl border border-gray-200">
                <video
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full"
                    poster=""
                >
                    <source src="/videos/como-usar.mp4" type="video/mp4" />
                    Tu navegador no soporta la reproducción de video.
                </video>
            </div>
        </div>

        {/* Funcionalidades */}
        <div className="max-w-5xl mx-auto px-6">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-10">¿Cómo lo hacemos?</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                    { icon: <TrendingUp size={24} />, title: "Proyección de tráfico", desc: "Analizamos patrones históricos para proyectar la congestión en tu hora de viaje.", color: "bg-blue-50 text-blue-600" },
                    { icon: <CloudSun size={24} />, title: "Clima en tu ruta", desc: "Condiciones meteorológicas esperadas para la hora exacta de tu viaje. Sin sorpresas.", color: "bg-sky-50 text-sky-600" },
                    { icon: <Users size={24} />, title: "Comunidad de viajeros", desc: "Mientras más viajeros registran, mejores son las proyecciones para todos.", color: "bg-green-50 text-green-600" },
                    { icon: <Smartphone size={24} />, title: "Conexión con tu GPS", desc: "Te enviamos directo a Waze o Google Maps. Nosotros planificamos, ellos navegan.", color: "bg-purple-50 text-purple-600" },
                    { icon: <AlertTriangle size={24} />, title: "Emergencias y servicios", desc: "Teléfonos de asistencia y servicios en ruta siempre a mano cuando los necesites.", color: "bg-orange-50 text-orange-600" },
                ].map((card, idx) => (
                    <div key={idx} className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className={`w-12 h-12 rounded-xl ${card.color} flex items-center justify-center mb-4`}>
                            {card.icon}
                        </div>
                        <h4 className="font-bold text-gray-900 mb-2">{card.title}</h4>
                        <p className="text-gray-500 text-sm leading-relaxed">{card.desc}</p>
                    </div>
                ))}
            </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 mt-12 text-center">
             <button onClick={handleStartTrip} className="bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl hover:bg-blue-700 transition-transform hover:-translate-y-1 inline-flex items-center gap-2">Comenzar ahora <ArrowRight size={20} /></button>
        </div>
    </div>
  );

  const renderSelection = () => (
    <div className="max-w-5xl mx-auto px-6 pb-20 animate-fade-in">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-gray-900 tracking-tight">¿Cuál es tu destino?</h2>
        <p className="text-lg text-gray-500 mt-3">Elige tu ruta, sentido y fecha para ver la proyección de tráfico.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="text-blue-600" size={20} /> Rutas Disponibles
          </h3>
          {ROUTES.map((route) => (
            <button 
              key={route.id}
                        onClick={() => {
                          setSelectedRouteId(route.id);
                          trackEvent('route_selected', {
                            routeId: route.id,
                            routeName: route.name,
                          });
                        }}
              className={`w-full flex items-center p-6 rounded-2xl border-2 transition-all text-left group hover:shadow-lg ${selectedRouteId === route.id ? 'border-blue-600 bg-white ring-4 ring-blue-50 shadow-md transform scale-[1.02]' : 'border-gray-100 bg-white hover:border-blue-200'}`}
            >
              <div className={`w-14 h-14 rounded-full flex items-center justify-center mr-5 shrink-0 transition-colors ${selectedRouteId === route.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400 group-hover:bg-blue-50 group-hover:text-blue-600'}`}>
                <Car size={26} />
              </div>
              <div className="flex-1">
                <h4 className={`font-bold text-xl ${selectedRouteId === route.id ? 'text-blue-700' : 'text-gray-900'}`}>{route.name}</h4>
                <p className="font-semibold text-sm text-gray-700">{route.destination}</p>
                <p className="text-xs text-gray-400">{route.subtext}</p>
              </div>
              {selectedRouteId === route.id && <div className="text-blue-600"><CheckCircle size={24} /></div>}
            </button>
          ))}
        </div>

        <div className="space-y-6">
          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-gray-100/50 h-full flex flex-col justify-between hover:shadow-2xl transition-all">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-6">Configuración del Viaje</h3>
              <div className="space-y-8">
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 block">Sentido del viaje</label>
                    <div className="flex flex-col gap-3">
                        {Object.values(Direction).map((dir) => (
                        <button key={dir} onClick={() => {
                          setDirection(dir);
                          trackEvent('direction_selected', {
                            direction: dir,
                            routeId: selectedRouteId,
                            tripDate,
                          });
                        }} className={`w-full p-4 rounded-xl text-left font-medium text-sm transition-all border flex justify-between items-center ${direction === dir ? 'bg-blue-50 text-blue-900 border-blue-200 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                            {dir}
                            {direction === dir && <div className="w-2 h-2 rounded-full bg-blue-500"></div>}
                        </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label htmlFor="tripDate" className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 block">Fecha de salida</label>
                    <div className="relative w-full h-[72px] group">
                        <div className="absolute inset-0 w-full h-full pl-12 pr-4 py-4 bg-white border border-gray-200 rounded-xl flex items-center justify-between shadow-sm group-hover:border-blue-400 transition-all pointer-events-none z-10">
                            <span className="font-bold text-gray-800 capitalize text-lg truncate">{getFormattedDate(tripDate)}</span>
                             <div className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg shrink-0 ml-2">Cambiar</div>
                        </div>
                        <CalendarIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-blue-500 transition-colors pointer-events-none z-20" size={20} aria-hidden="true" />
                        <input
                            id="tripDate"
                            type="date"
                            min={today}
                            value={tripDate}
                            onChange={(e) => { setTripDate(e.target.value); const idx = weatherForecast.findIndex(d => d.rawDate === e.target.value); if (idx !== -1) setSelectedWeatherIndex(idx); }} 
                            className="date-input-full absolute inset-0 w-full h-full opacity-0 z-30 cursor-pointer" 
                            required
                            aria-label="Fecha de salida del viaje"
                        />
                    </div>
                </div>
            </div>
            </div>
            <button onClick={handleContinue} className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-blue-700 transition-all mt-10 flex items-center justify-center gap-2">Registrar <ArrowRight size={20} /></button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPlanning = () => {
    const currentWeatherCondition = selectedWeather?.condition || 'sunny';
    const weatherRange = `${selectedWeather?.maxTemp ?? 0}° / ${selectedWeather?.minTemp ?? 0}°`;

    // Mínimo de hora permitido: si es hoy, la próxima media hora
    const minDepartureTime = getMinDepartureTimeForDate(tripDate);

    // Obtener texto de dirección legible
    const directionText = direction === Direction.SANTIAGO_LITORAL 
      ? 'Santiago → Litoral central' 
      : 'Litoral central → Santiago';

    return (
      <div className="max-w-5xl mx-auto px-6 pb-12 animate-fade-in">
        {/* Header: Ruta + Dirección + Clima */}
        <div className="mb-4">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900 mb-1">Tu viaje</h2>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="flex items-center gap-1.5 font-medium bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg text-xs border border-blue-100">
                    <MapPin size={14} /> {selectedRoute.name}
                  </span>
                  <span className="flex items-center gap-1.5 font-medium bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg text-xs border border-gray-200">
                    <ArrowRight size={14} /> {directionText}
                  </span>
                  <span className="text-xs text-gray-500 font-medium">{selectedWeather?.fullDate}</span>
                </div>
              </div>
              
              {/* Widget de clima a la derecha */}
              <div className="flex items-center gap-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg px-3 py-2.5 border border-blue-100">
                <div className="flex items-center justify-center">
                  {renderWeatherIcon(currentWeatherCondition, 24)}
                </div>
                <div>
                  <div className="text-base font-black text-gray-900">{weatherRange}</div>
                  <div className="text-[10px] text-gray-600 capitalize font-medium">
                    {currentWeatherCondition === 'rainy' ? 'Lluvia' : currentWeatherCondition === 'cloudy' ? 'Nublado' : 'Soleado'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gráfico comunitario + selector de hora — componente unificado, full-width, sin scroll */}
        <div className="mb-4">
          <Suspense fallback={
            <div className="h-96 rounded-2xl border border-slate-200 bg-white shadow-sm flex items-center justify-center">
              <Loader2 className="animate-spin text-gray-400" size={24} />
            </div>
          }>
            <CommunityTripsChart
              data={trafficData}
              loading={loadingTrafficData}
              direction={directionToApi(direction)}
              tripDate={tripDate}
              departureTime={departureTime}
              minDepartureTime={minDepartureTime}
              onDepartureTimeChange={setDepartureTime}
              onBack={handleBack}
              onConfirm={handleContinue}
              isSaving={isSaving}
              confirmDisabled={isSaving || loadingWeather || departureTime === null}
            />
          </Suspense>
        </div>

        {/* Pronóstico de duración (opcional, complementa la proyección IA) — link inline, sin card propia */}
        {departureTime !== null && canConsultRealtimeForecast && (
          <div className="mb-6">
            <button
              type="button"
              onClick={() => setShowNavigationForecast(c => !c)}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-gray-500 transition-colors hover:text-indigo-600"
            >
              <Clock size={12} />
              {showNavigationForecast ? 'Ocultar pronóstico' : 'Consultar pronóstico'}
            </button>

            {showNavigationForecast && (
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2.5 text-[11px] mt-2">
                <p className="font-semibold text-indigo-700 text-sm leading-tight">Pronóstico impulsado por IA y datos de navegación</p>
                <p className="mb-1.5 text-gray-400 text-[11px]">La precisión se va ajustando y puede variar al acercarse la hora de salida</p>
                {loadingEta ? (
                  <p className="text-gray-500">Consultando pronóstico...</p>
                ) : etaData && mostLikelyEtaMin !== null && displayOptimisticMin !== null && displayPessimisticMin !== null ? (
                  <div className="text-center space-y-0.5">
                    <p className="text-gray-500 font-medium">Lo más probable</p>
                    <p className="text-indigo-700 font-bold tabular-nums text-xl">
                      {formatDuration(mostLikelyEtaMin)}
                    </p>
                    <p className="text-gray-400 tabular-nums">
                      Puede variar entre {formatDuration(displayOptimisticMin)} y {formatDuration(displayPessimisticMin)}
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-500">Pronóstico no disponible.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Cambiar fecha de viaje — movido al final para no competir con la acción principal */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-6">
          <h3 className="font-bold text-gray-900 text-xs flex items-center gap-2 mb-3">
            <MapPin size={13} className="text-gray-400" />
            Cambiar fecha de viaje
            {loadingWeather && <Loader2 size={14} className="animate-spin text-blue-500" />}
          </h3>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {visibleWeatherDays.length > 0 ? (
              visibleWeatherDays.map((item) => {
                const dateObj = new Date(item.rawDate + 'T00:00:00');
                const dayNumber = dateObj.getDate();
                const isSelected = item.rawDate === tripDate;
                return (
                  <button
                    key={item.rawDate}
                    onClick={() => handleWeatherSelect(item)}
                    className={`flex-shrink-0 rounded-full border px-3 py-1.5 text-xs cursor-pointer transition-all ${
                      isSelected
                        ? 'border-indigo-300 bg-indigo-50 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/40'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className={`font-bold uppercase ${isSelected ? 'text-indigo-700' : 'text-gray-500'}`}>
                        {item.day.slice(0, 3)}
                      </span>
                      <span className={`font-black ${isSelected ? 'text-indigo-700' : 'text-gray-900'}`}>
                        {dayNumber}
                      </span>
                      {renderWeatherIcon(item.condition, 13)}
                      <span className="text-gray-400 font-medium">{item.maxTemp}°/{item.minTemp}°</span>
                    </div>
                  </button>
                );
              })
            ) : (
              <>
                {[1,2,3,4,5,6,7].map(i => (
                  <div key={i} className="flex-shrink-0 w-24 h-8 bg-gray-100 rounded-full animate-pulse"></div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Nota sobre Predicción - Mejorada y al final */}
        <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl border-2 border-blue-100 p-6 shadow-sm mt-6">
          <div className="flex items-start gap-4">
            <div className="bg-blue-100 rounded-xl p-3 flex-shrink-0">
              <TrendingUp className="text-blue-600" size={24} />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-gray-900 mb-2 text-sm">Mejorando con cada viaje</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Estamos desarrollando un sistema de predicción basado en inteligencia artificial que aprenderá de los datos históricos. 
                Cuantos más viajes se registren, más precisa y útil será la predicción para toda la comunidad.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderConfirmation = () => {
    // Obtener texto de dirección legible
    const directionText = direction === Direction.SANTIAGO_LITORAL 
      ? 'Santiago → Litoral central' 
      : 'Litoral central → Santiago';

    return (
      <div className="max-w-3xl mx-auto px-6 pb-20 animate-fade-in pt-10">
        <div className="bg-white rounded-[2rem] shadow-xl border border-gray-100 overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-400 to-blue-600"></div>
          <div className="p-10 text-center border-b border-gray-100 bg-gray-50/50">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6 shadow-sm animate-bounce-in">
                  {isSaving ? <Loader2 className="animate-spin text-green-600" size={30} /> : <CheckCircle className="text-green-600" size={40} />}
              </div>
              {user ? (
                  <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">
                        {editingTripId ? '¡Viaje actualizado!' : '¡Viaje Programado!'}
                      </h2>
                      <p className="text-gray-500">
                        {editingTripId
                          ? 'Actualizamos tu itinerario. Te notificaremos cualquier cambio.'
                          : 'Hemos guardado tu itinerario. Te notificaremos cualquier cambio.'}
                      </p>
                  </>
              ) : (
                  <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Viaje registrado</h2>
                      <div className="flex flex-col items-center gap-2">
                        <button onClick={() => handleOpenAuth('register')} className="bg-gray-900 text-white px-6 py-3 rounded-xl font-bold text-sm shadow-lg hover:bg-gray-800 transition-all inline-flex items-center gap-2"><Bell size={18} /> Guardar mi viaje</button>
                      </div>
                  </>
              )}
          </div>
          <div className="p-10">
              <div className="space-y-6 max-w-md">
                  <div><p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Ruta</p><p className="text-xl font-bold text-gray-900">{selectedRoute.name}</p></div>
                  <div><p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Sentido</p><p className="text-lg font-medium text-gray-800">{directionText}</p></div>
                  <div><p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Fecha</p><p className="text-lg font-medium text-gray-800 capitalize">{selectedWeather?.fullDate}</p></div>
                  <div className="flex items-start gap-4 pt-2">
                       <div className="bg-blue-50 p-3 rounded-xl"><Clock className="text-blue-600" size={24} /></div>
                       <div><p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Ingreso a autopista</p><p className="text-2xl font-bold text-gray-900">{departureTime !== null ? formatTime(departureTime) : '--:--'}</p></div>
                  </div>
                  {(() => {
                    const coords = ROUTE_COORDINATES[selectedRouteId];
                    if (!coords) return null;
                    const goingToCoast = direction === Direction.SANTIAGO_LITORAL;
                    // Punto de referencia conocido en vez de coordenada genérica, solo para el
                    // lado litoral — Plaza Vergara (ruta 68, Viña del Mar) y Plaza de Armas
                    // (ruta 78, San Antonio) — pedido directo.
                    // Nunca se fija el origen: se omite a propósito para que Waze/Maps usen la
                    // ubicación actual del usuario como punto de partida (comportamiento nativo).
                    const coastalLandmark: Record<string, string> = {
                      '68': 'Plaza Vergara, Viña del Mar, Chile',
                      '78': 'Plaza de Armas de San Antonio, Chile',
                    };
                    const landmark = goingToCoast ? coastalLandmark[selectedRouteId] : undefined;
                    const dest = goingToCoast ? coords.destination : coords.origin;
                    const mapsHref = landmark
                      ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(landmark)}&travelmode=driving`
                      : `https://www.google.com/maps/dir/?api=1&destination=${dest.lat},${dest.lon}&travelmode=driving`;
                    // Waze no se ofrece en la web: su página de respaldo (waze.com/live-map)
                    // devuelve 403 desde el servidor de Waze para cualquier visita sin la app
                    // instalada — confirmado directo contra su servidor, no es algo que podamos
                    // arreglar desde acá. En Android/iOS sí funciona porque el sistema operativo
                    // intercepta el link antes de llegar a esa página rota.
                    return (
                      <div className="pt-2">
                        <a
                          href={mapsHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <Compass size={16} /> Abrir en Maps
                        </a>
                      </div>
                    );
                  })()}
              </div>
          </div>
        {user && (
            <div className="px-10 py-6 border-t border-gray-100 bg-gray-50/50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Bell className="text-gray-600" size={20} />
                        <div>
                            <p className="font-medium text-gray-900">Notificaciones</p>
                            <p className="text-sm text-gray-500">Recibe alertas sobre cambios en el tráfico</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-1 rounded">Próximamente</span>
                        <label className="relative inline-flex items-center opacity-40 cursor-not-allowed" title="Próximamente disponible">
                            <input
                                type="checkbox"
                                checked={false}
                                disabled
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 rounded-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                        </label>
                    </div>
                </div>
            </div>
        )}
        <div className="p-6 bg-gray-50 border-t border-gray-200 flex flex-col items-center gap-3">
             <button onClick={handleStartTrip} className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition-all">
               Programa otro viaje o tu retorno
             </button>
             <button onClick={() => setStep('planned-trips')} className="text-gray-500 font-medium text-sm hover:text-gray-700 transition-colors">
               Ver planificados
             </button>
        </div>
      </div>
      

      {/* Recuadro de Comunidad - Fuera del card */}
      <div className="mt-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-green-100 p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="bg-green-100 rounded-xl p-3 flex-shrink-0">
            <Users className="text-green-600" size={24} />
          </div>
          <div className="flex-1">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Comunidad</p>
            <p className="text-lg font-semibold text-gray-900 mb-2">
              Ya muchos usuarios como tú registran su viaje
            </p>
            <CommunityMessage />
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-green-200/50 mt-3">
              <p className="text-xs text-gray-600 leading-relaxed">
                <strong className="text-green-700">Mientras más registros, más precisión.</strong> Cada viaje que se registra ayuda a mejorar las predicciones para toda la comunidad.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <PreviewBanner />
      {(() => {
        const currentTab =
          step === 'planned-trips' || step === 'planned-trip-detail' ? 'planned-trips'
            : step === 'trip-history' ? 'trip-history'
            : step === 'services' ? 'services'
            : step === 'profile' || step === 'emergency' || step === 'my-vehicle' ? 'profile'
            : 'planner';
        return (
      <TopNav
        variant={step === 'landing' || step === 'how-it-works' ? 'landing' : 'app'}
        currentTab={currentTab}
        onNavigateToPlanner={handleStartTrip}
        onNavigateToPlannedTrips={handleOpenPlannedTrips}
        onNavigateToTripHistory={handleOpenTripHistory}
        onNavigateToServices={() => setStep('services')}
        onNavigateToEmergency={() => { setEmergencyOrigin(currentTab === 'services' ? 'services' : 'profile'); setStep('emergency'); }}
        onNavigateToLanding={() => setStep('landing')}
        onNavigateToProfile={() => setStep('profile')}
        onOpenAuth={handleOpenAuth}
        hideAuthActions={step === 'selection' || step === 'planning' || step === 'confirmation'}
      />
        );
      })()}
      <Suspense fallback={null}>
        <AuthModal isOpen={isAuthModalOpen} onClose={() => setAuthModalOpen(false)} initialView={authView} />
      </Suspense>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {(step === 'landing' || step === 'how-it-works') && <SmartAppBanner />}
      
      {step !== 'confirmation' &&
        step !== 'landing' &&
        step !== 'how-it-works' &&
        step !== 'privacy' &&
        step !== 'terms' &&
        step !== 'profile' &&
        step !== 'my-vehicle' &&
        step !== 'services' &&
        step !== 'planned-trips' &&
        step !== 'planned-trip-detail' &&
        step !== 'trip-history' &&
        step !== 'emergency' && <StepWizard currentStep={step} />}

      <main
        className={`${
          step !== 'landing' &&
          step !== 'how-it-works' &&
          step !== 'privacy' &&
          step !== 'terms' &&
          step !== 'profile' &&
          step !== 'my-vehicle' &&
          step !== 'services' &&
          step !== 'planned-trips' &&
          step !== 'planned-trip-detail' &&
          step !== 'trip-history' &&
          step !== 'emergency'
            ? 'mt-6'
            : ''
        }`}
      >
        {step === 'landing' && renderLanding()}
        {step === 'how-it-works' && renderHowItWorks()}
        {step === 'selection' && renderSelection()}
        {step === 'planning' && renderPlanning()}
        {step === 'confirmation' && renderConfirmation()}
        {step === 'privacy' && <PrivacyPolicy onBack={() => setStep('landing')} />}
        {step === 'terms' && <TermsOfService onBack={() => setStep('landing')} />}
        {step === 'planned-trips' && (
          <PlannedTripsPage
            onGoToPlanner={handleStartTrip}
            onViewTripDetail={handleOpenPlannedTripDetail}
            onRequireAuth={() => handleOpenAuth('login')}
          />
        )}
        {step === 'planned-trip-detail' && selectedPlannedTrip && (
          <PlannedTripDetailPage
            trip={selectedPlannedTrip}
            onBack={handleBackToPlannedTrips}
            onEditTrip={handleEditTripFromPlanned}
            onDeleteTrip={handleDeletePlannedTrip}
          />
        )}
        {step === 'trip-history' && <TripHistoryPage onRequireAuth={() => handleOpenAuth('login')} />}
        {step === 'profile' && (
          <ProfilePage
            onBack={() => setStep('landing')}
            onNavigateToEmergency={() => { setEmergencyOrigin('profile'); setStep('emergency'); }}
            onNavigateToMyVehicle={() => setStep('my-vehicle')}
            onChangeSession={() => handleOpenAuth('login')}
          />
        )}
        {step === 'emergency' && <EmergencyPage onBack={() => setStep(emergencyOrigin)} />}
        {step === 'my-vehicle' && <MyVehiclePage onBack={() => setStep('profile')} onRequireAuth={() => handleOpenAuth('login')} />}
        {step === 'services' && (
          <ServiciosEnRutaPage
            onNavigateToEmergency={() => { setEmergencyOrigin('services'); setStep('emergency'); }}
          />
        )}
      </main>
    </div>
  );
}

export default function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}
